import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { keptIds, prepareBriefings } from '../../utils/events/briefingCache';
import SaveAreaButton from '../map/SaveAreaButton';

/*
 * "Prepare for the event" (Part III section 17, PR12): before setting out,
 * keep the event's briefing videos on this phone (Cache Storage) and save the
 * area's map for offline use (SaveAreaButton, which estimates and asks
 * first). A forest has no signal; the briefing then plays from the phone.
 */
export default function PrepareForEvent({ event }) {
  const { t } = useTranslation('events');
  const [briefings, setBriefings] = useState([]);
  const [kept, setKept] = useState(new Set());
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(`/events/${event.mission_id}/briefings`)
      .then(async (r) => {
        const items = r.data.items || [];
        if (!live) return;
        setBriefings(items);
        const have = await keptIds(items).catch(() => new Set());
        if (live) setKept(have);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [event.mission_id]);

  const prepare = async () => {
    setProgress({ done: 0, total: briefings.length });
    await prepareBriefings(briefings, { onProgress: (done, total) => setProgress({ done, total }) });
    setKept(await keptIds(briefings).catch(() => new Set()));
    setProgress(null);
  };
  const bbox = event.area?.bbox || [];
  // The area's middle; an event with no area (0087) saves the map round its meeting point.
  const center = bbox.every((v) => v != null) && bbox.length === 4
    ? { lat: (Number(bbox[0]) + Number(bbox[1])) / 2, lng: (Number(bbox[2]) + Number(bbox[3])) / 2 }
    : event.meeting ? { lat: Number(event.meeting.lat), lng: Number(event.meeting.lng) } : null;

  return (
    <section data-testid="event-prepare" aria-labelledby="event-prepare-title"
      style={{ border: '1px solid var(--c-border)', borderRadius: 12, padding: 10, marginTop: 10 }}>
      <h3 id="event-prepare-title" style={{ margin: '0 0 6px', fontSize: 15 }}>{t('prepare.title')}</h3>
      <p style={{ margin: '0 0 8px', fontSize: 13 }}>
        {briefings.length ? t('prepare.videos', { kept: kept.size, total: briefings.length }) : t('prepare.noVideos')}
      </p>
      {briefings.length > 0 && kept.size < briefings.length && (
        <button type="button" className="pn-btn" style={{ minHeight: 44 }} onClick={prepare} disabled={!!progress}
          data-testid="event-prepare-videos">
          {progress ? t('prepare.downloading', progress) : t('prepare.keep')}
        </button>
      )}
      {center && <div style={{ marginTop: 8 }}><SaveAreaButton center={center} label={event.area?.name_he || event.meeting?.label || ''} compact /></div>}
    </section>
  );
}
